import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { colors, fontFamily } from '../theme';

//Images
import CakeStakeText from "../images/CakeStakesText.png"

export default function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [maintenance, setMaintenance] = useState(null);
  const [warning, setWarning] = useState(null);

  useEffect(() => {
    document.title = 'Login | CakeStakes';
  }, []);

  useEffect(() => {
    checkMaintenance();
    checkWarning();
    const interval = setInterval(() => {
      checkMaintenance();
      checkWarning();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const checkMaintenance = async () => {
    try {
      const response = await api.getMaintenanceStatus();
      setMaintenance(response.is_maintenance ? response : null);
    } catch (err) {
      // Ignore errors - maintenance check is optional
    }
  };

  const checkWarning = async () => {
    try {
      const response = await api.getWarning();
      setWarning(response.has_warning ? response : null);
    } catch (err) {
      // Ignore errors
    }
  };

  const formatEndTime = (isoString) => {
    if (!isoString) return null;
    try {
      const date = new Date(isoString);
      return date.toLocaleString();
    } catch {
      return isoString;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await api.login(username, password);
      if (result.user) {
        onLogin(result.user);
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      const response = err.response?.data;
      if (response?.maintenance) {
        setMaintenance({
          is_maintenance: true,
          message: response.message,
          estimated_end: response.estimated_end
        });
        setError(response.error || 'System is under maintenance');
      } else {
        setError(response?.error || 'Please wait 30 seconds then retry.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: colors.bgGradient, border: '12px solid transparent', borderImage: 'linear-gradient(135deg, #BF953F, #eccd78, #B38728, #eccd78, #AA771C) 1', boxShadow: 'inset 0 0 0 0px #BF953F, 0 0 0 2px #AA771C' }}>
      <div className="rounded-lg shadow-2xl p-8 max-w-md w-full" style={{ background: colors.surfaceBg }}>
        <img src={CakeStakeText} alt="CakeStakeLogo" className="mx-auto mb-2 w-full" />
        <p className="text-center text-gray-600 mb-6"> </p>

        {warning && (
          <div className="p-4 mb-4 rounded" style={{ background: 'rgba(255,193,7,0.08)', border: '1px solid rgba(255,193,7,0.5)', borderLeft: '4px solid #ffc107' }}>
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 mt-0.5" style={{ color: '#ffc107' }} viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-bold mb-1" style={{ fontFamily, color: '#ffc107', fontSize: '1rem' }}>Notice</h3>
                <p className="text-sm" style={{ fontFamily, color: colors.textPrimary, fontWeight: 'bold', fontSize: '0.9rem' }}>{warning.message}</p>
              </div>
            </div>
          </div>
        )}

        {maintenance && (
          <div className="p-4 mb-4 rounded" style={{ background: 'rgba(0,0,0,0.3)', border: '0px solid #BF953F' }}>
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5" style={{ color: colors.textPrimary }} viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-bold" style={{ fontFamily, color: colors.textPrimary, fontSize: '1.25rem' }}>
                  System Maintenance
                </h3>
                <div className="mt-2 text-sm" style={{ fontFamily, color: colors.textPrimary, fontWeight: 'bold', fontSize: '1rem' }}>
                  <p>{maintenance.message || 'System is under maintenance. Please check back later.'}</p>
                  {maintenance.estimated_end && (
                    <p className="mt-1">
                      Estimated end: {formatEndTime(maintenance.estimated_end)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="px-4 py-3 rounded mb-4" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid #BF953F', color: colors.textPrimary, fontFamily, fontWeight: 'bold' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-4xl font-bold mb-1" style={{ fontFamily, color: colors.textPrimary }}>Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-400"
              style={{ background: 'rgba(0,0,0,0.3)', color: colors.textPrimary, fontFamily, border: '0px solid #BF953F', fontWeight: 'bold', fontSize: '1.25rem' }}
              required
            />
          </div>

          <div>
            <label className="block text-4xl font-bold mb-1" style={{ fontFamily, color: colors.textPrimary }}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-400"
              style={{ background: 'rgba(0,0,0,0.3)', color: colors.textPrimary, fontFamily, border: '0px solid #BF953F', fontWeight: 'bold', fontSize: '1.25rem' }}
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 rounded disabled:bg-gray-400 font-bold"
            style={{ background: 'linear-gradient(135deg, #BF953F, #FCF6BA, #B38728)', color: '#2d0a2d', fontFamily, fontSize: '1.5rem' }}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
}
